import React, { useEffect, useState } from "react";
import { X, Volume2, VolumeX, Music, Music2, Mic, MicOff, User } from "lucide-react";
import { audio } from "../../game/audio";

export const AudioSettings = ({ open, onClose }) => {
  const [muted, setMuted]     = useState(audio.muted);
  const [volume, setVolume]   = useState(audio.volume);
  const [voiceOn, setVoiceOn] = useState(audio.voiceOn);
  const [musicOn, setMusicOn] = useState(audio.musicOn);
  const [voices, setVoices]   = useState(audio.getVoices());
  const [voiceName, setVoiceName] = useState(audio.preferredVoiceName || "");

  useEffect(() => {
    if (!open) return;
    // Unlock audio synchronously on open so any tweak plays immediately
    audio.unlock();
    if (!audio.ambient) audio.startAmbient("exploration");
    // Refresh voice list — may have loaded async since last time
    const refresh = () => setVoices(audio.getVoices());
    refresh();
    if ("speechSynthesis" in window) {
      window.speechSynthesis.addEventListener("voiceschanged", refresh);
    }
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => {
      window.removeEventListener("keydown", h);
      if ("speechSynthesis" in window) {
        window.speechSynthesis.removeEventListener("voiceschanged", refresh);
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const applyMute = (m) => { audio.unlock(); setMuted(m); audio.setMuted(m); };
  const applyVolume = (v) => { audio.unlock(); setVolume(v); audio.setVolume(v); };
  const applyVoice = (on) => {
    audio.unlock();
    setVoiceOn(on);
    audio.setVoiceOn(on);
    if (on) audio.speak("Narration enabled.");
  };
  const applyMusic = (on) => { audio.unlock(); setMusicOn(on); audio.setMusicOn(on); };
  const applyVoicePick = (name) => {
    audio.unlock();
    setVoiceName(name);
    audio.setPreferredVoice(name);
    // Preview
    audio.speak("This is the voice of Blackwood Annex.");
  };

  // English voices preferred; group but present as flat list
  const enVoices = voices.filter((v) => /en(-|_|$)/i.test(v.lang));
  const others = voices.filter((v) => !/en(-|_|$)/i.test(v.lang));
  const orderedVoices = [...enVoices, ...others];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="audio-modal">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-deepshadow border border-white/15 p-8 mx-4 animate-fadein">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-mono text-xs tracking-[0.35em] uppercase text-white/70">// AUDIO</h3>
          <button onClick={onClose} className="text-white/50 hover:text-emergency transition-colors" data-testid="audio-close">
            <X size={18} />
          </button>
        </div>

        {/* Master volume */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => applyMute(!muted)}
                className="text-white/70 hover:text-emergency transition-colors"
                data-testid="audio-mute-toggle"
              >
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/60">MASTER VOLUME</span>
            </div>
            <span className="font-mono text-xs text-white/80 tabular-nums">{Math.round(volume * 100)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(volume * 100)}
            onChange={(e) => applyVolume(parseInt(e.target.value, 10) / 100)}
            data-testid="audio-volume-slider"
            className="w-full accent-red-800 h-1 bg-white/15 appearance-none cursor-pointer"
          />
        </div>

        {/* Music toggle */}
        <label className="flex items-center justify-between mb-4 cursor-pointer group">
          <span className="flex items-center gap-3">
            {musicOn ? <Music size={16} className="text-white/70" /> : <Music2 size={16} className="text-white/40" />}
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/60 group-hover:text-white/80">MUSIC</span>
          </span>
          <button
            onClick={() => applyMusic(!musicOn)}
            className={`relative w-12 h-6 border ${musicOn ? "border-emergency bg-blood/40" : "border-white/20 bg-black/50"} transition-colors`}
            data-testid="audio-music-toggle"
          >
            <span className={`absolute top-0.5 ${musicOn ? "right-0.5" : "left-0.5"} w-5 h-4 ${musicOn ? "bg-emergency" : "bg-white/50"} transition-all`} />
          </button>
        </label>

        {/* Voice toggle */}
        <label className="flex items-center justify-between mb-4 cursor-pointer group">
          <span className="flex items-center gap-3">
            {voiceOn ? <Mic size={16} className="text-white/70" /> : <MicOff size={16} className="text-white/40" />}
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/60 group-hover:text-white/80">NARRATION</span>
          </span>
          <button
            onClick={() => applyVoice(!voiceOn)}
            className={`relative w-12 h-6 border ${voiceOn ? "border-emergency bg-blood/40" : "border-white/20 bg-black/50"} transition-colors`}
            data-testid="audio-voice-toggle"
          >
            <span className={`absolute top-0.5 ${voiceOn ? "right-0.5" : "left-0.5"} w-5 h-4 ${voiceOn ? "bg-emergency" : "bg-white/50"} transition-all`} />
          </button>
        </label>

        {/* Voice picker */}
        <div className={`mb-6 ${voiceOn ? "" : "opacity-40 pointer-events-none"}`}>
          <div className="flex items-center gap-3 mb-2">
            <User size={14} className="text-white/60" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/55">NARRATOR VOICE</span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={voiceName}
              onChange={(e) => applyVoicePick(e.target.value)}
              data-testid="audio-voice-picker"
              className="flex-1 bg-black/85 border border-white/20 focus:border-emergency outline-none px-3 py-2 text-sm text-white font-mono cursor-pointer transition-colors"
            >
              <option value="">Auto — deepest available (recommended)</option>
              {orderedVoices.map((v) => (
                <option key={v.name} value={v.name}>
                  {v.name} — {v.lang}
                </option>
              ))}
            </select>
            <button
              onClick={() => { audio.unlock(); audio.speak("Some doors, once opened, stay open."); }}
              data-testid="audio-voice-test"
              className="term-btn"
              title="Test voice"
            >
              TEST
            </button>
          </div>
          {orderedVoices.length === 0 && (
            <p className="mt-2 text-[11px] text-white/40 italic">
              Voices load a moment after opening this menu — reopen if the list is empty.
            </p>
          )}
        </div>

        <p className="narrative text-xs text-white/40 italic leading-relaxed">
          Narration reads each scene aloud in a clean voice. The whispers come through in a lower, closer voice.
          Every sound is generated in your browser — nothing streams from outside.
        </p>
      </div>
    </div>
  );
};
